import has from 'lodash.has';
import sortBy from 'lodash.sortby';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { PERIOD } from '../../constants/enrollment';

dayjs.extend(isBetween);

/**
 * Parse date string with respect of time zone.
 * Native Date constructor treats date-only strings in ISO 8601 format (e.g. "1970-01-01")
 * as UTC, not local. This method provides a courtesy of local time zone for those.
 * If provided date string includes time zone information, it will be honored by dayjs.js
 * @param {*} date Date.parse compatible date format: Date object or string
 * @return {Date} date object with parsed date and 00:00 in local time zone
 */
export const parseDate = date => dayjs(date).toDate();

/**
 * Sort all bmWindows by startDate field: BOY < MOY < EOY
 * @param {Array<Object>} bmWindows windows to sort
 * @returns {Array<Object>} sorted windows
 */
const getSortedPeriods = bmWindows =>
    sortBy(
        bmWindows
            .filter(
                bmWindow =>
                    has(bmWindow, 'startDate') &&
                    has(bmWindow, 'endDate') &&
                    has(bmWindow, 'period'),
            )
            .map(bmWindow => ({
                ...bmWindow,
                startDate: parseDate(bmWindow.startDate),
                endDate: parseDate(bmWindow.endDate),
            })),
        ['startDate'],
    );

const getEndOfCurrentDay = () =>
    dayjs()
        .endOf('day')
        .toDate();
/**
 * Determines in what benchmark period provided date falls in:
 *     before BOY           -> BOY
 *     during BOY           -> BOY
 *     after BOY before MOY -> BOY
 *     during MOY           -> MOY
 *     after MOY before EOY -> MOY
 *     during EOY           -> EOY
 *     after EOY            -> EOY
 *
 * Example:
 *    bmWindows = {
 *      BOY: {
 *          startDate: 01.01.2019,
 *          endDate: 31.01.2019,
 *      },
 *      MOY: {
 *          startDate: 15.02.2019,
 *          endDate: 28.02.2019,
 *      },
 *      EOY: {
 *          startDate: 15.03.2019,
 *          endDate: 31.03.2019,
 *      },
 *    }
 *
 *    Date === 01.12.2018 => Period === BOY.
 *    Date === 15.01.2019 => Period === BOY.
 *    Date === 10.02.2019 => Period === BOY.
 *    Date === 20.02.2019 => Period === MOY.
 *    Date === 10.03.2019 => Period === MOY.
 *    Date === 20.03.2019 => Period === EOY.
 *    Date === 10.04.2019 => Period === EOY.
 * @param {Array<Object>} bmWindows BM windows object
 * @param {Date} date date
 * @returns {Object} with application benchmark window data
 */
export const getBenchmarkWindowByDate = (bmWindows, date) => {
    if (!bmWindows?.length) {
        return undefined;
    }
    const sortedPeriods = getSortedPeriods(bmWindows);

    return sortedPeriods.reduce((acc, val) => {
        if (val.startDate <= date) {
            return val;
        }
        return acc;
    }, sortedPeriods[0]);
};

/**
 * Get bm window with maximum startDate that is less or equal current date
 * If no window found - return BOY by default
 * @param {Array<Object>} bmWindows BM windows object
 * @returns {Object} with application benchmark window data
 */
export const getCurrentBenchmarkWindow = bmWidows => {
    return getBenchmarkWindowByDate(bmWidows, getEndOfCurrentDay());
};

/**
 * Determine next BM window based on windows dates:
 *    during BOY and before MOY  -> MOY
 *    during MOY and before EOY  -> EOY
 *    undefined in other cases
 *
 * @param {Array<Object>} bmWindows BM windows dates
 * @returns {Object|undefined} next window object
 */
export const getNextBenchmarkWindow = bmWindows => {
    if (!bmWindows?.length) {
        return undefined;
    }
    const sortedPeriods = getSortedPeriods(bmWindows);

    // doubt it can be different from 3, but just in case
    if (sortedPeriods.length < 3) {
        return undefined;
    }

    const endOfCurrentDay = getEndOfCurrentDay();
    const [periodBOY, periodMOY, periodEOY, periodSUMMER] = sortedPeriods;

    let goalPeriod;

    if (endOfCurrentDay >= periodBOY.startDate && endOfCurrentDay < periodMOY.startDate) {
        // if current date is between BOY and MOY, then next goal period is MOY
        goalPeriod = periodMOY;
    } else if (endOfCurrentDay >= periodMOY.startDate && endOfCurrentDay < periodEOY.startDate) {
        // if current date is between EOY and MOY, then next goal period is EOY
        goalPeriod = periodEOY;
    } else if (
        periodSUMMER &&
        endOfCurrentDay >= periodEOY.startDate &&
        endOfCurrentDay < periodSUMMER.startDate
    ) {
        // if summer period was set and current data is between EOY and summer,
        // than the next goal period is summer
        goalPeriod = periodSUMMER;
    }

    // otherwise next goal period is not defined
    return goalPeriod;
};

/**
 * Given two dates, return difference between them in days, disregarding hours and lower units.
 * E.g.: 2018-08-20 (Monday) - 2018-08-20 (Monday) = 0; 2018-08-22 (Wednesday) - 2018-08-20 (Monday) = 2
 *
 * @param {Date} dateEarlier: the earlier date in time.
 * @param {Date} dateLater: the later date in time.
 * @return {int}
 */
export const getDaysDiff = (dateEarlier, dateLater) =>
    dateLater <= dateEarlier
        ? 0
        : Math.ceil(
              (dateLater.setUTCHours(0, 0, 0, 0) - dateEarlier.setUTCHours(0, 0, 0, 0)) /
                  (1000 * 3600 * 24),
          );

/**
 * Check if current date is within the BM window start and end dates inclusivly
 * @param {Object} bmWindow current benchmark window
 * @returns {Boolean} true if current date if between BM window dates
 */
export const isCurrentDateWithinBMPeriod = (bmWindow, period) =>
    bmWindow.period === period &&
    dayjs().isBetween(dayjs(bmWindow.startDate), dayjs(bmWindow.endDate), 'day', '[]');

/**
 * Check if current date appears to be before BOY assessment window with precision of days
 * @param {Object} bmWindow current benchmark window
 * @returns {Boolean} true if current date is before BOY window start date
 */
export const isCurrentDateBeforeBOYPeriod = bmWindow =>
    bmWindow.period === PERIOD.BOY && dayjs().isBefore(dayjs(bmWindow.startDate, 'day'));

export const getStartDay = date => dayjs(date).startOf('day');
